import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCreatePrivacyPolicy,
  usePrivacyPolicy,
  useUpdatePrivacyPolicy,
} from "@/hooks/usePrivacyPolicy";

type PrivacyPolicyContentItem = {
  title: string;
  items: string[];
};

type PrivacyPolicyType = {
  id: number | null;
  title: string;
  description: string;
  content: PrivacyPolicyContentItem[];
};

const initialData: PrivacyPolicyType = {
  id: null,
  title: "",
  description: "",
  content: [
    {
      title: "",
      items: [""],
    },
  ],
};

const normalizeContent = (content: unknown): PrivacyPolicyContentItem[] => {
  if (Array.isArray(content)) {
    return content.map((section) => ({
      title: typeof section?.title === "string" ? section.title : "",
      items: Array.isArray(section?.items)
        ? section.items.map((item: unknown) => String(item))
        : [""],
    }));
  }

  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);

      if (Array.isArray(parsed)) {
        return parsed.map((section) => ({
          title: typeof section?.title === "string" ? section.title : "",
          items: Array.isArray(section?.items)
            ? section.items.map((item: unknown) => String(item))
            : [""],
        }));
      }
    } catch {
      return initialData.content;
    }
  }

  return initialData.content;
};

export default function PrivacyPolicyEditor() {
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = location.pathname.includes("/edit");

  const createPrivacyPolicy = useCreatePrivacyPolicy();
  const updatePrivacyPolicy = useUpdatePrivacyPolicy();

  const { data: existingData, isLoading: isFetchingData } =
    usePrivacyPolicy(isEditMode);

  const [data, setData] = useState<PrivacyPolicyType>(initialData);
  const [isSaving, setIsSaving] = useState(false);

  const isLoading = isEditMode && isFetchingData;

  useEffect(() => {
    if (!isEditMode || !existingData) return;

    const content = normalizeContent(existingData.content);

    setData({
      id: existingData.id ?? null,
      title: existingData.title ?? "",
      description: existingData.description ?? "",
      content,
    });
  }, [isEditMode, existingData]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSectionTitleChange = (sectionIndex: number, value: string) => {
    setData((prev) => {
      const newContent = [...prev.content];

      newContent[sectionIndex] = {
        ...newContent[sectionIndex],
        title: value,
      };

      return {
        ...prev,
        content: newContent,
      };
    });
  };

  const handleItemChange = (
    sectionIndex: number,
    itemIndex: number,
    value: string
  ) => {
    setData((prev) => {
      const newContent = [...prev.content];
      const newItems = [...newContent[sectionIndex].items];

      newItems[itemIndex] = value;

      newContent[sectionIndex] = {
        ...newContent[sectionIndex],
        items: newItems,
      };

      return {
        ...prev,
        content: newContent,
      };
    });
  };

  const addSection = () => {
    setData((prev) => ({
      ...prev,
      content: [
        ...prev.content,
        {
          title: "",
          items: [""],
        },
      ],
    }));
  };

  const removeSection = (sectionIndex: number) => {
    setData((prev) => ({
      ...prev,
      content: prev.content.filter((_, index) => index !== sectionIndex),
    }));
  };

  const addItem = (sectionIndex: number) => {
    setData((prev) => {
      const newContent = [...prev.content];

      newContent[sectionIndex] = {
        ...newContent[sectionIndex],
        items: [...newContent[sectionIndex].items, ""],
      };

      return {
        ...prev,
        content: newContent,
      };
    });
  };

  const removeItem = (sectionIndex: number, itemIndex: number) => {
    setData((prev) => {
      const newContent = [...prev.content];
      const newItems = newContent[sectionIndex].items.filter(
        (_, index) => index !== itemIndex
      );

      newContent[sectionIndex] = {
        ...newContent[sectionIndex],
        items: newItems.length > 0 ? newItems : [""],
      };

      return {
        ...prev,
        content: newContent,
      };
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsSaving(true);

    const payload = {
      title: data.title,
      description: data.description,
      content: data.content,
    };

    try {
      if (isEditMode) {
        await updatePrivacyPolicy.mutateAsync(payload);
      } else {
        await createPrivacyPolicy.mutateAsync(payload);
      }

      navigate("/privacy-policy");
    } catch (err) {
      console.error("Gagal menyimpan privacy policy:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Memuat data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {isEditMode ? "Edit Privacy Policy" : "Tambah Privacy Policy"}
          </h2>
          <p className="text-muted-foreground">
            {isEditMode
              ? "Ubah konten kebijakan privasi."
              : "Tambahkan konten kebijakan privasi."}
          </p>
        </div>

        <Link to="/privacy-policy">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Kembali
          </Button>
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Informasi Utama</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Judul</label>
              <input
                name="title"
                value={data.title}
                onChange={handleChange}
                placeholder="Contoh: Kebijakan Privasi"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Deskripsi</label>
              <textarea
                name="description"
                value={data.description}
                onChange={handleChange}
                placeholder="Masukkan deskripsi singkat kebijakan privasi"
                className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Konten Privacy Policy</CardTitle>

            <Button type="button" variant="outline" onClick={addSection}>
              <Plus className="mr-2 h-4 w-4" />
              Tambah Section
            </Button>
          </CardHeader>

          <CardContent className="space-y-6">
            {data.content.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                className="space-y-4 rounded-lg border p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-2">
                    <label className="text-sm font-medium">
                      Judul Section {sectionIndex + 1}
                    </label>
                    <input
                      value={section.title}
                      onChange={(e) =>
                        handleSectionTitleChange(sectionIndex, e.target.value)
                      }
                      placeholder="Contoh: Informasi yang Kami Kumpulkan"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeSection(sectionIndex)}
                    disabled={data.content.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Poin Konten</label>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addItem(sectionIndex)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Tambah Poin
                    </Button>
                  </div>

                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start gap-3">
                      <textarea
                        value={item}
                        onChange={(e) =>
                          handleItemChange(
                            sectionIndex,
                            itemIndex,
                            e.target.value
                          )
                        }
                        placeholder="Masukkan isi poin kebijakan privasi"
                        className="min-h-20 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                        required
                      />

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeItem(sectionIndex, itemIndex)}
                        disabled={section.items.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Link to="/privacy-policy">
            <Button type="button" variant="outline">
              Batal
            </Button>
          </Link>

          <Button
            type="submit"
            disabled={
              isSaving ||
              createPrivacyPolicy.isPending ||
              updatePrivacyPolicy.isPending
            }
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ||
            createPrivacyPolicy.isPending ||
            updatePrivacyPolicy.isPending
              ? "Menyimpan..."
              : "Simpan"}
          </Button>
        </div>
      </form>
    </div>
  );
}